import { apiService } from '../../../components/api/apiService';
import { authservice } from '../../../components/api/authservice';
import { logGovernedMcp } from './governedMcpLogger';
import type { QuotaDirectory, QuotaDirectoryDepartment, QuotaDirectoryRole, QuotaDirectoryUser } from './governedMcpTypes';

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function asList(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
  if (value && typeof value === 'object') {
    const container = value as Record<string, unknown>;
    if (Array.isArray(container.data)) return asList(container.data);
    if (Array.isArray(container.items)) return asList(container.items);
    if (Array.isArray(container.users)) return asList(container.users);
    if (Array.isArray(container.departments)) return asList(container.departments);
  }
  return [];
}

function displayName(row: Record<string, unknown>): string {
  const parts = [asString(row.firstName || row.first_name), asString(row.lastName || row.last_name)].filter(Boolean);
  return parts.join(' ') || asString(row.email) || asString(row.id || row.userId);
}

export const EMPTY_QUOTA_DIRECTORY: QuotaDirectory = { users: [], roles: [], departments: [] };

export function parseQuotaUsers(raw: unknown): QuotaDirectoryUser[] {
  return asList(raw)
    .map((row) => {
      const id = asString(row.id || row.userId || row.userID || row.user_id);
      if (!id) return null;
      return {
        id,
        name: displayName(row),
        email: asString(row.email),
        roleId: asString(row.roleID || row.roleId || row.role_id || row.RoleID),
        roleName: asString(row.roleName || row.role_name),
        departmentId: asString(row.department_id || row.departmentId || row.departmentID || row.DepartmentId),
      } satisfies QuotaDirectoryUser;
    })
    .filter((row): row is QuotaDirectoryUser => Boolean(row));
}

export function parseQuotaRoles(raw: unknown): QuotaDirectoryRole[] {
  return asList(raw)
    .map((row) => {
      const id = asString(row.roleId || row.role_id || row.id);
      if (!id) return null;
      return {
        id,
        name: asString(row.roleName || row.role_name || row.name) || id,
      } satisfies QuotaDirectoryRole;
    })
    .filter((row): row is QuotaDirectoryRole => Boolean(row));
}

export function parseQuotaDepartments(raw: unknown): QuotaDirectoryDepartment[] {
  return asList(raw)
    .map((row) => {
      const departmentId = asString(row.department_id || row.departmentId);
      const rowId = asString(row.id);
      const id = departmentId || rowId;
      if (!id) return null;
      const matchIds = Array.from(new Set([id, departmentId, rowId].filter(Boolean)));
      return {
        id,
        name: asString(row.department_name || row.departmentName || row.name) || id,
        matchIds,
      } satisfies QuotaDirectoryDepartment;
    })
    .filter((row): row is QuotaDirectoryDepartment => Boolean(row));
}

export async function loadQuotaDirectory(): Promise<QuotaDirectory> {
  try {
    const [usersRaw, rolesRaw, departmentsRaw] = await Promise.all([
      authservice.getUsers(),
      authservice.getRoles(),
      apiService.get('/api/MasterData/departments', {}, 'backend'),
    ]);
    const users = parseQuotaUsers(usersRaw);
    const roles = parseQuotaRoles(rolesRaw);
    const departments = parseQuotaDepartments(departmentsRaw);
    const roleNameById = new Map(roles.map((role) => [role.id, role.name]));
    logGovernedMcp('app', 'quota_directory_loaded', {
      users: users.length,
      roles: roles.length,
      departments: departments.length,
    });
    return {
      users: users.map((user) => ({
        ...user,
        roleName: user.roleName || roleNameById.get(user.roleId) || '',
      })),
      roles,
      departments,
    };
  } catch (err) {
    logGovernedMcp('warning', 'quota_directory_failed', {
      message: err instanceof Error ? err.message : String(err),
    });
    return EMPTY_QUOTA_DIRECTORY;
  }
}

export function departmentMatches(department: QuotaDirectoryDepartment, departmentId: string): boolean {
  if (!departmentId) return false;
  return department.matchIds.includes(departmentId) || department.id === departmentId;
}
